import React from "react"
import { useRouteMatch, Redirect } from "react-router-dom"

import { SvgBackgroundBox } from "components/marketing/svg-background-box"
import Accordion, { Section } from "components/panels/accordion"
import { BrowserView, MobileView } from "components/helpers/hide"
import { MainContainer } from "components/main-container"
import { Eyebrow, ParagraphSmall } from "components/text"
import { MetaTags } from "components/meta/meta-tags"
import { Promo } from "components/marketing/promo"
import { Separator } from "components/separator"
import Grid, { Col } from "components/grid"
import { Box } from "components/box"

import { OngoingSupport } from "./components/ongoing-support"
import { TopPanel } from "./components/top-panel"

import { LearnPostIndex } from "pages/learn/learn-types"
import contentfulPosts from "contentful/post.json"
import { Markdown } from "theme/markdown-html"
import { baseRoutes } from "routes"
import theme from "theme"

import Bottle from "assets/backgrounds/blog-cove-bottle-dt.jpg"

const Blob = () => (
  <svg width="1440px" height="429px" viewBox="0 0 1440 429" xmlns="http://www.w3.org/2000/svg">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <path
        d="M1538.0018,276.170375 C1647.11083,390.589912 1664.74203,642.312894 1564.57636,736.775535 C1464.15517,831.238177 1245.68159,768.440477 1092.11127,729.591053 C938.28542,690.741629 848.851789,676.106572 742.809054,578.184735 C636.510795,479.996807 513.347908,298.788191 558.064724,223.218078 C602.526015,147.381873 815.122533,177.716355 1021.58646,181.17555 C1228.05039,184.634745 1428.63724,161.750838 1538.0018,276.170375 Z"
        fill={theme.colors.iceBlue}
        fillRule="nonzero"
        transform="translate(1089.638668, 479.291569) rotate(-11.000000) translate(-1089.638668, -479.291569) "
      ></path>
      <path
        d="M512.959465,-282.681803 C590.203011,-168.262266 602.68499,83.4607156 531.772881,177.923357 C460.679875,272.385998 306.011884,209.588298 197.292044,170.738874 C88.3913068,131.88945 25.0769245,117.254393 -49.9958431,19.3325563 C-125.249509,-78.8553721 -212.442458,-260.063988 -180.785267,-335.634101 C-149.308974,-411.470306 1.19835748,-381.135824 147.364132,-377.676629 C293.529906,-374.217433 435.53502,-397.101341 512.959465,-282.681803 Z"
        fill={theme.colors.iceBlue}
        fillRule="nonzero"
        transform="translate(195.541573, -79.560609) rotate(-11.000000) translate(-195.541573, 79.560609) "
      ></path>
    </g>
  </svg>
)

export const LearnPost = () => {
  const {
    params: { path = "" }
  } = useRouteMatch<{ path?: string }>()
  const { post: posts } = contentfulPosts as LearnPostIndex
  const post = posts[path]

  if (!post) {
    return <Redirect to={baseRoutes.notFound()} />
  }

  const {
    imgUrl,
    imgAlt,
    section,
    header,
    content,
    disclaimer,
    metaTitle,
    metaDescription,
    ogTitle,
    ogDescription,
    ogImg
  } = post

  let disclaimerContent: Maybe<Section[]>

  if (disclaimer) {
    disclaimerContent = [
      {
        header: "Dislaimer",
        content: <Markdown content={disclaimer} />,
        isOpen: true
      }
    ]
  }

  return (
    <>
      <MetaTags
        title={`${metaTitle} - Cove`}
        description={metaDescription}
        ogTitle={ogTitle}
        ogDescription={ogDescription}
        ogImage={ogImg}
        absoluteOgImageUrl
      />
      <MainContainer
        mainContent={
          <>
            <TopPanel section={section} content={header} imgSrc={imgUrl} imgAlt={imgAlt} />
            <Grid>
              <Col cols={[4, 4, 10, 8]} push={[0, 0, 1, 2]}>
                <Box my={[5, 7, 7, 7]}>
                  <Markdown content={content} />
                  {disclaimerContent && (
                    <>
                      <Separator borderColor="silver" mb={[4, 4, 0, 0]} />
                      <BrowserView>
                        <Accordion
                          sections={disclaimerContent}
                          headerProps={{
                            fontSize: "1.125rem"
                          }}
                          contentProps={{
                            fontSize: "0.875rem"
                          }}
                        />
                      </BrowserView>
                      <MobileView>
                        <Eyebrow>{disclaimerContent[0].header}</Eyebrow>
                        <ParagraphSmall mt={5}>{disclaimerContent[0].content}</ParagraphSmall>
                      </MobileView>
                    </>
                  )}
                </Box>
              </Col>
            </Grid>

            <SvgBackgroundBox svg={Blob} py={[7, 7, 9, 9]}>
              <OngoingSupport />
              <Promo imgSrc={Bottle} mobileImgSrc={Bottle} />
            </SvgBackgroundBox>
          </>
        }
      />
    </>
  )
}
